import { useEffect, useRef, useState, useCallback } from "react";
import { Search } from "lucide-react";

import { FilterBadgeRow, SortOption } from "./filter-badge-row";

import { useMediaQuery } from "@/hooks/use-media-query";
import { isDesktopSize } from "utils/is-desktop";

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";

import { useFetch } from "modules/main/hooks/useFetch";
import { endPointCatalogSearch, filterParam } from "modules/main/services/endpoints";
import { CatalogResponse } from "modules/catalog/types/catalogResponse";
import { SearchResults } from "modules/catalog/components/searchResults";
import { Payload, ProductSearchSelectionContext, ProductSearchSelectionHandler } from "modules/cart/types/cart";
import { boolean } from "zod";

const DEBOUNCE_MS = 400;
const MIN_QUERY_LENGTH = 2;

const EMPTY_FILTERS: filterParam[] = [
    { name: "brand", value: [] },
    { name: "tag", value: [] },
];

interface ProductSearchPickerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /**
     * Fired when the user hits "add" on any result card.
     * The picker closes automatically after this fires.
     */
    onProductSelected: ProductSearchSelectionHandler;
    /** Pre-populates the search input (e.g. the raw list-item description). */
    initialQuery?: string;
}

/**
 * Reusable Sheet/Dialog that embeds the catalog search results.
 * On mobile: bottom Sheet. On desktop: centered Dialog.
 *
 * Designed to be decoupled from cart logic — it receives an `onProductSelected`
 * callback and lets the caller decide what to do with the chosen product.
 *
 * @param props.open - Controls open state.
 * @param props.onOpenChange - Called when the user closes the picker.
     * @param props.onProductSelected - Fired with the Payload and optional selected product context.
 * @param props.initialQuery - Optional string to pre-populate the search input.
 */
const ProductSearchPicker: React.FC<ProductSearchPickerProps> = ({
    open,
    onOpenChange,
    onProductSelected,
    initialQuery = "",
}) => {

    // Filter and sort state — reset on every sheet open cycle
    const [activeFilters, setActiveFilters] = useState<filterParam[]>(EMPTY_FILTERS);
    const [sortBy, setSortBy] = useState<SortOption>("relevance");

    // Pagination state
    const [currentPage, setCurrentPage] = useState<string | null>(null);
    const [prevPageCursor, setPrevPageCursor] = useState<string | null>(null);

    /**
     * Toggles a single filter value inside the matching filterParam entry.
     * @param filterName - The filter group name (e.g. "brand")
     * @param value - The display_name value to toggle
     */
    const handleFilterToggle = (filterName: string, value: string) => {
        setActiveFilters((prev) =>
            prev.map((f) =>
                f.name === filterName
                    ? {
                          ...f,
                          value: f.value.includes(value)
                              ? f.value.filter((v) => v !== value)
                              : [...f.value, value],
                      }
                    : f
            )
        );
    };

    /** Resets all active filters; preserves sortBy. */
    const handleClearAll = () => {
        setActiveFilters(EMPTY_FILTERS);
    };

    // Live input value (shown to the user immediately)
    const [inputValue, setInputValue] = useState(initialQuery);
    // Debounced value actually used for the API call
    const [debouncedQuery, setDebouncedQuery] = useState(
        initialQuery.length >= MIN_QUERY_LENGTH ? initialQuery : "",
    );
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // When the picker opens, reset to the initialQuery and clear filter/sort state
    useEffect(() => {
        if (open) {
            setInputValue(initialQuery);
            setDebouncedQuery(
                initialQuery.length >= MIN_QUERY_LENGTH ? initialQuery : "",
            );
            setActiveFilters(EMPTY_FILTERS);
            setSortBy("relevance");
            // Reset pagination state
            setCurrentPage(null);
            setPrevPageCursor(null);
        }
    }, [open, initialQuery]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (value.length < MIN_QUERY_LENGTH) {
            setDebouncedQuery("");
            return;
        }

        debounceRef.current = setTimeout(() => {
            setDebouncedQuery(value);
        }, DEBOUNCE_MS);
    };

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const searchUrl =
        debouncedQuery.length >= MIN_QUERY_LENGTH
            ? endPointCatalogSearch(debouncedQuery, currentPage, sortBy, activeFilters, prevPageCursor)
            : null;

    const { data, error, loading } = useFetch<CatalogResponse>(searchUrl);

    /** Handle page navigation via CursorPaginator. */
    const handlePageNavigate = useCallback((nextCursor: string | null, prevCursor: string | null) => {
        setPrevPageCursor(currentPage);
        setCurrentPage(nextCursor);

        // Reset scroll position to top when navigating pages
        const resultsContainer = document.querySelector('[class*="overflow-y-auto"]');
        if (resultsContainer) {
            resultsContainer.scrollTop = 0;
        }
    }, [currentPage]);

    /** Intercepts ProductCard's add action, notifies caller, then closes. */
    const handleProductSelected = (payload: Payload, context?: ProductSearchSelectionContext) => {
        if (context) {
            onProductSelected(payload, context);
        } else {
            onProductSelected(payload);
        }
        onOpenChange(false);
    };

    const content = (
        <div className="flex flex-col gap-3 min-h-0 flex-1">
            {/* Search input */}
            <div className="relative px-1 flex-shrink-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    autoFocus
                    placeholder="Buscar producto..."
                    className="pl-9"
                    value={inputValue}
                    onChange={handleInputChange}
                />
            </div>

            {/* Filter/sort badge strip — only after first search */}
            {debouncedQuery.length >= MIN_QUERY_LENGTH && (
                <FilterBadgeRow
                    availableFilters={data?.filters ?? []}
                    activeFilters={activeFilters}
                    sortBy={sortBy}
                    onFilterToggle={handleFilterToggle}
                    onSortChange={setSortBy}
                    onClearAll={handleClearAll}
                />
            )}

            {/* Results area — scrollable, never grows outside the container */}
            <div className="flex-1 min-h-0 overflow-y-auto">
                {debouncedQuery.length < MIN_QUERY_LENGTH ? (
                    <p className="text-sm text-muted-foreground text-center mt-4 px-4">
                        Escribe al menos {MIN_QUERY_LENGTH} caracteres para buscar.
                    </p>
                ) : (
                    <SearchResults
                        section={debouncedQuery}
                        brand_filters={activeFilters.find((f) => f.name === "brand") ?? EMPTY_FILTERS[0]}
                        tag_filters={activeFilters.find((f) => f.name === "tag") ?? EMPTY_FILTERS[1]}
                        currentPage={currentPage}
                        search_params=""
                        data={data ?? ({} as CatalogResponse)}
                        error={error}
                        loading={loading}
                        attribution_token={null}
                        overrideAddCallback={handleProductSelected}
                        onNavigate={handlePageNavigate}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="bottom" className="h-[85dvh] flex flex-col gap-3 overflow-hidden">
                <SheetHeader className="flex-shrink-0">
                    <SheetTitle>Buscar producto</SheetTitle>
                    <SheetDescription>
                        Busca el producto que quieres agregar al carrito.
                    </SheetDescription>
                </SheetHeader>
                {content}
            </SheetContent>
        </Sheet>
    );
};

export { ProductSearchPicker };
export type { ProductSearchSelectionContext, ProductSearchSelectionHandler };
